import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { fetchPendingAccounts } from "@/api/globalApiService";
import { useState, useEffect, useRef } from "react";
import { EAdAccountStatus, IAccount } from "@/types";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import Loader from "@/components/common/loaders/SectionLoader";
import { LuListChecks, LuListX, LuSettings2, LuSquarePlus } from "react-icons/lu";
import emptyAccount from "@/assets/emptyAccount.svg";
import styles from "./PostOAuthDialog.module.css";
import { cn } from "@/lib/utils";

const POST_OAUTH_DIALOG_TITLE = "Connect ad accounts to";
const POST_OAUTH_DIALOG_TITLE_PLATFORM = import.meta.env.VITE_APP_SITE_TITLE as string;
const POST_OAUTH_DIALOG_DESCRIPTION =
  "Select the ad accounts you want to connect from available options";
const POST_OAUTH_DIALOG_SELECT_ALL_BUTTON_TEXT = "Select All";
const POST_OAUTH_DIALOG_CLEAR_BUTTON_TEXT = "Clear Selection";
const EMPTY_ACCOUNT_HEADER = "No ad accounts available";
const EMPTY_ACCOUNT_DESCRIPTION =
  "You have no ad accounts linked. Try\nconnecting a different account";

export default function PostOauthDialog({
  open,
  onOpenChange,
  platform,
  onAddAccount,
  onConnectAccounts,
  connectingAccounts,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  platform: string;
  onAddAccount: () => void;
  onConnectAccounts: (accounts: IAccount[]) => Promise<void>;
  connectingAccounts?: boolean;
}) {
  const [pendingAccounts, setPendingAccounts] = useState<IAccount[]>([]);
  const [selectedAccounts, setSelectedAccounts] = useState<IAccount[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const allAccounts = useRef<IAccount[]>([]);

  useEffect(() => {
    fetchPendingAccounts(platform)
      .then((accounts) => {
        allAccounts.current = accounts;
        setPendingAccounts(accounts);
      })
      .catch((error) => console.error(error))
      .finally(() => setLoading(false));
  }, [platform]);

  const handleAccountSelection = (account: IAccount, checked: boolean) => {
    if (checked) {
      setSelectedAccounts([...selectedAccounts, account]);
    } else {
      setSelectedAccounts(selectedAccounts.filter((item) => item.id !== account.id));
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    if (e.target.value === "") {
      setPendingAccounts(allAccounts.current);
      return;
    }
    const filteredAccounts = allAccounts.current.filter(
      (account) =>
        account.name.toLowerCase().includes(e.target.value.toLowerCase()) ||
        account.ad_account_id.toLowerCase().includes(e.target.value.toLowerCase()),
    );
    setPendingAccounts(filteredAccounts);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex flex-col items-start justify-center h-[500px] !rounded-[4px] w-[600px] max-w-[600px] gap-0">
        {loading ? (
          <Loader
            icon={<LuSettings2 size={24} className="text-brand-orange" />}
            bgColor="bg-brand-orange"
          />
        ) : (
          <>
            {pendingAccounts.length === 0 && searchQuery === "" ? (
              <div className="flex flex-col items-center justify-center h-full w-full gap-[32px]">
                <img src={emptyAccount} alt="Empty Account" />
                <div className="text-center">
                  <p className="text-[14px] font-500 text-brand-primary-new">
                    {EMPTY_ACCOUNT_HEADER}
                  </p>
                  <p className="text-sm text-gray-500 whitespace-pre text-center">
                    {EMPTY_ACCOUNT_DESCRIPTION}
                  </p>
                </div>
                <Button
                  variant="outline"
                  className="h-[40px] bg-[#021859] text-[14px] font-500 text-[#fff] rounded-[40px] hover:bg-[#021859] hover:text-[#fff] hover:opacity-80"
                  onClick={onAddAccount}
                >
                  <LuSquarePlus />
                  <span className="ml-[4px]">Add account</span>
                </Button>
              </div>
            ) : (
              <>
                <DialogHeader>
                  <DialogTitle className="text-[16px] font-600 text-brand-primary-new">
                    {POST_OAUTH_DIALOG_TITLE}&nbsp;
                    <span className="text-brand-orange">
                      {POST_OAUTH_DIALOG_TITLE_PLATFORM.toLowerCase()}
                    </span>
                  </DialogTitle>
                  <DialogDescription className="text-[14px] font-400 text-[#617390]">
                    {POST_OAUTH_DIALOG_DESCRIPTION}
                  </DialogDescription>
                </DialogHeader>
                <Input
                  placeholder="Search"
                  value={searchQuery}
                  onChange={handleSearch}
                  className="border-border-gray mt-[14px] rounded-[4px] h-[32px] shadow-none py-[6px] px-[12px] focus-visible:ring-transparent"
                />
                <div
                  className={cn(
                    "flex flex-col gap-[8px] h-full w-full text-brand-primary-new text-[14px] font-400 overflow-y-auto mt-[24px] pr-[18px]",
                    styles.scrollbarmodifier,
                  )}
                >
                  {pendingAccounts.map((account) => (
                    <div
                      className="flex items-center gap-[8px] w-full justify-between"
                      key={account.id}
                    >
                      <div className="flex items-center gap-[8px]">
                        <Checkbox
                          key={account.id}
                          checked={
                            selectedAccounts.includes(account) ||
                            account.status !== EAdAccountStatus.NOT_CONNECTED
                          }
                          disabled={account.status !== EAdAccountStatus.NOT_CONNECTED}
                          onCheckedChange={(checked) =>
                            handleAccountSelection(account, checked as boolean)
                          }
                          className="border-[#021859] data-[state=checked]:bg-[#021859] data-[state=checked]:text-[#fff] data-[state=checked]:border-[#021859]"
                        />
                        <p>{account.name}</p>
                      </div>
                      {account.status !== EAdAccountStatus.NOT_CONNECTED && (
                        <p className="text-sm text-gray-500 text-right">Connected</p>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex justify-between gap-[8px] w-full">
                  <div className="flex gap-[8px]">
                    <Button
                      variant="outline"
                      onClick={() =>
                        setSelectedAccounts(
                          pendingAccounts.filter(
                            (account) =>
                              account.status === EAdAccountStatus.NOT_CONNECTED,
                          ),
                        )
                      }
                      disabled={
                        selectedAccounts.length === pendingAccounts.length ||
                        !pendingAccounts.some(
                          (account) => account.status === EAdAccountStatus.NOT_CONNECTED,
                        )
                      }
                      className="h-[32px] py-[6px] px-[12px] rounded-[32px] text-brand-primary-new text-[11px] font-500 border-[#021859] disabled:border-[#B2B2B2] disabled:text-[#B2B2B2]"
                    >
                      <LuListChecks />
                      <span className="ml-[4px]">
                        {POST_OAUTH_DIALOG_SELECT_ALL_BUTTON_TEXT}
                      </span>
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setSelectedAccounts([])}
                      className="h-[32px] py-[6px] px-[12px] rounded-[32px] text-brand-primary-new text-[11px] font-500 border-[#021859] disabled:border-[#B2B2B2] disabled:text-[#B2B2B2]"
                      disabled={selectedAccounts.length === 0}
                    >
                      <LuListX />
                      <span className="ml-[4px]">
                        {POST_OAUTH_DIALOG_CLEAR_BUTTON_TEXT}
                      </span>
                    </Button>
                  </div>
                  <Button
                    variant="brand"
                    className="h-[32px] py-[6px] px-[12px] rounded-[32px] text-[11px] font-500 bg-brand-orange disabled:bg-[#B2B2B2]"
                    disabled={selectedAccounts.length === 0 || connectingAccounts}
                    onClick={() => {
                      void onConnectAccounts(selectedAccounts);
                    }}
                  >
                    <span className="mr-[4px]">
                      {connectingAccounts ? "Connecting..." : "Connect"}
                    </span>
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
